import React, { useCallback, useEffect, useRef, useState } from "react"
import { observer } from "mobx-react"
import { Box, useTheme } from '@mui/material'
import { debounce, Icons } from "../../utils/virtualboard-utilities"
import MenuBar from './MenuBar'
import { useVirtualboardStore } from '../../store/virtualboard.store'

const getCustomCursorOffset = ( tool ) => {
    let x = 0, y = 0
    switch ( tool ) {
        case "rect":
        case "circle":
            x = -6
            y = -10
            break
        case "pen":
        case "pencil":
        case "marker":
            y = -15
            break
        case "select":
            y = -4
            x = -3
            break
        case "text":
            x = -6
            y = -10
            break
        default:
            break

    }
    return { x, y }
}

function wrapText ( ctx, text = "", x, y, maxWidth, lineHeight ) {
    const words = text.split( " " )
    let line = ""
    let lines = 0
    const startY = y

    for ( let n = 0; n < words.length; n++ ) {
        const testLine = line + words[n] + " "
        const testWidth = ctx.measureText( testLine ).width

        if ( testWidth > maxWidth && n > 0 ) {
            ctx.fillText( line, x, y )
            line = words[n] + ""
            y += lineHeight
            lines++
        } else {
            line = testLine
        }
    }

    // Draw last line
    ctx.fillText( line, x, y )
    lines++

    // Compute total height of rendered text
    const totalHeight = ( lines - 1 ) * lineHeight + lineHeight

    // Return both for further use
    return totalHeight
}

const ReadOnlyVirtualBoard = observer( ( {
    bgImage,
    board,
    elements,
    lastHundredCursorPoints = { current: [] },
    tool = "pen",
    pressed,
} ) => {

    const store = useVirtualboardStore?.() // if you don't need store here, you can remove it

    const canvasWrapperRef = useRef( null )
    const canvasCursorRef = useRef( null )
    const tempCanvasRef = useRef( null )
    const drawCanvasRef = useRef( null )
    const containerRef = useRef( null )
    const bgCanvasRef = useRef( null )

    const drawCanvasCtxRef = useRef( null )
    const bgCanvasCtxRef = useRef( null )
    const tempCanvasCtxRef = useRef( null )

    const bgImgRef = useRef( null )

    const { palette } = useTheme()

    // ---------- Helpers: CSS-based sizes & denormalization ----------

    const getCanvasCSSSize = useCallback( () => {
        const canvas = drawCanvasRef.current
        if ( !canvas ) return { width: 0, height: 0 }
        const rect = canvas.getBoundingClientRect()
        return { width: rect.width, height: rect.height }
    }, [] )

    const denormalizePoint = useCallback( ( p ) => {
        const { width, height } = getCanvasCSSSize()
        return { x: p.x * width, y: p.y * height }
    }, [getCanvasCSSSize] )

    const denormalizeWidth = useCallback( ( len ) => {
        const { width } = getCanvasCSSSize()
        return len * width
    }, [getCanvasCSSSize] )

    const denormalizeHeight = useCallback( ( len ) => {
        const { height } = getCanvasCSSSize()
        return len * height
    }, [getCanvasCSSSize] )

    // ---------- Fullscreen & Download ----------

    const toggleFullScreen = () => {
        if ( !window.document.fullscreenElement ) {
            if ( containerRef.current ) {
                const el = containerRef.current
                if ( el.requestFullscreen ) el.requestFullscreen()
                else if ( el.webkitRequestFullscreen ) el.webkitRequestFullscreen()
                else if ( el.msRequestFullscreen ) el.msRequestFullscreen()
                store?.setContainerState?.( { fullscreen: true } )
            }
        } else {
            store?.setContainerState?.( { fullscreen: false } )
            window.document.exitFullscreen()
        }
    }

    const downloadMergedCanvas = () => {
        const bgCanvas = bgCanvasRef.current
        const drawCanvas = drawCanvasRef.current
        if ( !bgCanvas || !drawCanvas ) return

        const mergedCanvas = document.createElement( "canvas" )
        const width = bgCanvas.width
        const height = bgCanvas.height
        mergedCanvas.width = width
        mergedCanvas.height = height

        const ctx = mergedCanvas.getContext( "2d" )
        ctx.drawImage( bgCanvas, 0, 0, width, height )
        ctx.drawImage( drawCanvas, 0, 0, width, height )

        mergedCanvas.toBlob( ( blob ) => {
            const link = document.createElement( "a" )
            link.href = URL.createObjectURL( blob )
            link.download = "virtualboard.png"
            link.click()
            URL.revokeObjectURL( link.href )
        }, "image/png" )
    }

    // ---------- Drawing primitives ----------

    const drawCurve = useCallback( ( ctx, element ) => {
        let { lineJoin, points, lineWidth = 1, composite, lineDash, colorInfo, opacity } = element
        if ( !points || points.length < 2 ) return

        const denormPoints = points.map( p => denormalizePoint( p ) )

        const rgb = colorInfo ?? { r: 0, g: 0, b: 0 }
        const color = `rgba(${rgb.r},${rgb.g},${rgb.b},${opacity})`

        ctx.save()
        ctx.lineWidth = lineWidth
        ctx.lineCap = "round"
        ctx.lineJoin = lineJoin ?? "round"
        ctx.strokeStyle = color
        if ( composite ) ctx.globalCompositeOperation = composite
        ctx.setLineDash( lineDash || [] )
        ctx.beginPath()

        ctx.moveTo( denormPoints[0].x, denormPoints[0].y )

        for ( let i = 1; i < denormPoints.length - 1; i++ ) {
            const p = denormPoints[i]
            const next = denormPoints[i + 1]
            const xc = ( p.x + next.x ) / 2
            const yc = ( p.y + next.y ) / 2
            ctx.quadraticCurveTo( p.x, p.y, xc, yc )
        }

        const last = denormPoints[denormPoints.length - 1]
        ctx.lineTo( last.x, last.y )

        ctx.stroke()
        ctx.restore()
    }, [denormalizePoint] )

    const drawElement = useCallback( ( ctx, element ) => {
        const {
            type,
            fill,
            centerX,
            centerY,
            x: startX,
            y: startY,
            width,
            height,
            radiusX,
            radiusY,
            fontSize,
            text,
            fontFamily = "Roboto",
            lineJoin,
            lineCap,
            lineWidth,
            colorInfo,
            opacity,
            composite,
            lineDash,
        } = element

        ctx.save()

        const rgb = colorInfo ?? { r: 0, g: 0, b: 0 }
        const color = `rgba(${rgb.r},${rgb.g},${rgb.b},${opacity})`

        ctx.lineWidth = lineWidth
        ctx.strokeStyle = color
        ctx.fillStyle = color
        ctx.lineCap = lineCap ?? "round"
        ctx.lineJoin = lineJoin ?? "round"
        if ( composite ) ctx.globalCompositeOperation = composite
        ctx.setLineDash( lineDash || [] )

        if ( type === "stroke" ) {
            drawCurve( ctx, element )
        } else if ( type === "rect" ) {
            const x = denormalizeWidth( startX )
            const y = denormalizeHeight( startY )
            const w = denormalizeWidth( width )
            const h = denormalizeHeight( height )

            if ( fill ) ctx.fillRect( x, y, w, h )
            else ctx.strokeRect( x, y, w, h )
        } else if ( type === "circle" ) {
            const cx = denormalizeWidth( centerX )
            const cy = denormalizeHeight( centerY )
            const rx = denormalizeWidth( radiusX )
            const ry = denormalizeHeight( radiusY )

            ctx.beginPath()
            ctx.ellipse( cx, cy, rx, ry, 0, 0, Math.PI * 2 )
            if ( fill ) ctx.fill()
            else ctx.stroke()
        } else if ( type === "text" ) {
            const fontPx = denormalizeHeight( fontSize )
            ctx.font = `${fontPx}px ${fontFamily}`
            ctx.fillStyle = color

            const pos = denormalizePoint( { x: startX, y: startY } )
            const textBoxWidth = denormalizeWidth( width )

            wrapText(
                ctx,
                text,
                pos.x,
                pos.y + fontPx,
                textBoxWidth,
                fontPx * 1.2
            )
        }

        ctx.restore()
    }, [drawCurve, denormalizeHeight, denormalizePoint, denormalizeWidth] )

    // ---------- Full redraw (background + elements) ----------

    const redrawAll = useCallback( () => {
        const bgCtx = bgCanvasCtxRef.current
        const drawCtx = drawCanvasCtxRef.current
        if ( !bgCtx || !drawCtx || !bgImgRef.current ) return

        const dpr = window.devicePixelRatio || 1

        // reset to identity in backing pixels
        bgCtx.setTransform( 1, 0, 0, 1, 0, 0 )
        drawCtx.setTransform( 1, 0, 0, 1, 0, 0 )

        // clear full backing store
        bgCtx.clearRect( 0, 0, bgCtx.canvas.width, bgCtx.canvas.height )
        drawCtx.clearRect( 0, 0, drawCtx.canvas.width, drawCtx.canvas.height )

        const zoom = board?.zoom ?? 1
        const panNorm = board?.pan ?? { x: 0, y: 0 }   // normalized pan
        const panCss = denormalizePoint( panNorm )
        const { width: boardW, height: boardH } = getCanvasCSSSize()

        const scale = zoom * dpr
        const offsetX = panCss.x * dpr
        const offsetY = panCss.y * dpr

        // Apply DPR * zoom, with pan in CSS→backing
        bgCtx.setTransform( scale, 0, 0, scale, offsetX, offsetY )
        drawCtx.setTransform( scale, 0, 0, scale, offsetX, offsetY )

        // Draw background in CSS coordinates
        bgCtx.drawImage( bgImgRef.current, 0, 0, boardW, boardH )

        // Draw all elements
        elements.forEach( el => drawElement( drawCtx, el ) )
    }, [elements, board?.zoom, board?.pan, drawElement, denormalizePoint, getCanvasCSSSize] )

    // ---------- Initial canvas setup ----------

    const setupCanvases = useCallback( ( img ) => {
        bgImgRef.current = img

        const canvasWrapper = canvasWrapperRef.current
        const drawCanvas = drawCanvasRef.current
        const bgCanvas = bgCanvasRef.current
        const tempCanvas = tempCanvasRef.current

        if ( !drawCanvas || !bgCanvas || !tempCanvas || !containerRef.current ) return

        const aspect = img.naturalWidth / img.naturalHeight
        const { width: containerWidth, height: containerHeight } = containerRef.current.getBoundingClientRect()

        let width = containerWidth * 0.9
        let height = width / aspect
        const maxHeight = containerHeight * 0.98

        if ( height > maxHeight ) {
            height = maxHeight
            width = height * aspect
        }

        canvasWrapper.style.width = `${width}px`
        canvasWrapper.style.height = `${height}px`

        const dpr = window.devicePixelRatio || 1

            ;[bgCanvas, drawCanvas, tempCanvas].forEach( ( c ) => {
                c.width = width * dpr
                c.height = height * dpr

                c.style.width = `${width}px`
                c.style.height = `${height}px`

                c.style.position = "absolute"
                c.style.top = 0
                c.style.left = 0
            } )

        bgCanvasCtxRef.current = bgCanvas.getContext( "2d" )
        drawCanvasCtxRef.current = drawCanvas.getContext( "2d" )
        tempCanvasCtxRef.current = tempCanvas.getContext( "2d" )

        redrawAll()
    }, [redrawAll] )

    // ---------- Effects ----------

    // Load background image
    useEffect( () => {
        const newImage = bgImage
        if ( newImage ) {
            const img = new Image()
            img.crossOrigin = "anonymous" // if needed
            img.src = newImage
            img.onload = () => setupCanvases( img )
        }
    }, [bgImage, setupCanvases] )

    // Redraw when board or elements change
    useEffect( () => {
        redrawAll()
    }, [redrawAll] )

    // Cursor trail on temp canvas
    useEffect( () => {
        try {
            const canvas = tempCanvasRef.current
            if ( !canvas ) return
            const ctx = canvas.getContext( "2d" )
            let animationFrameId
            const trail = []

            const fadeCursor = () => {
                let latestZoom = 1, latestPan = { x: 0, y: 0 }, latest
                if ( lastHundredCursorPoints?.current?.length ) {
                    latest = lastHundredCursorPoints.current[lastHundredCursorPoints.current.length - 1]
                    latestZoom = latest.zoom
                    latestPan = latest.pan
                }

                ctx.setTransform( 1, 0, 0, 1, 0, 0 )
                ctx.clearRect( 0, 0, ctx.canvas.width, ctx.canvas.height )

                ctx.save()
                ctx.globalCompositeOperation = "destination-out"
                ctx.fillStyle = "rgba(0, 0, 0, 0.25)"
                ctx.fillRect( 0, 0, canvas.width, canvas.height )
                ctx.restore()

                if ( lastHundredCursorPoints?.current?.length ) {
                    trail.push( { ...latest, t: Date.now() } )
                    if ( trail.length > 15 ) trail.shift()
                }

                const now = Date.now()
                for ( let i = 0; i < trail.length; i++ ) {
                    const p = trail[i]
                    const age = now - p.t
                    const life = 300
                    if ( age > life ) continue

                    const fade = 1 - age / life
                    const pixelX = p.canvasPos.x * canvas.width
                    const pixelY = p.canvasPos.y * canvas.height
                    const radius = 2 * fade

                    const gradient = ctx.createRadialGradient( pixelX, pixelY, 0, pixelX, pixelY, radius * 2 )
                    gradient.addColorStop( 0, pressed ? `rgba(255,0,0,${fade})` : `rgba(0,0,0,${fade})` )
                    gradient.addColorStop( 0.6, pressed ? `rgba(255,0,0,${fade * 0.4})` : `rgba(0,0,0,${fade * 0.4})` )
                    gradient.addColorStop( 1, pressed ? "rgba(255,0,0,0)" : "rgba(0,0,0,0)" )

                    ctx.fillStyle = gradient
                    ctx.beginPath()
                    ctx.arc( pixelX, pixelY, radius * 2, 0, Math.PI * 2 )
                    ctx.fill()

                    if ( canvasCursorRef.current && lastHundredCursorPoints?.current?.length ) {
                        const cursorOffset = getCustomCursorOffset( tool )
                        const pos = lastHundredCursorPoints.current.at( -1 ).canvasPos
                        canvasCursorRef.current.style.top = `${denormalizeHeight( pos.y ) + cursorOffset.y}px`
                        canvasCursorRef.current.style.left = `${denormalizeWidth( pos.x ) + cursorOffset.x}px`
                    }
                }

                animationFrameId = requestAnimationFrame( fadeCursor )
            }

            fadeCursor()

            return () => cancelAnimationFrame( animationFrameId )
        } catch ( err ) {
            console.log( err )
        }
    }, [pressed, tool, lastHundredCursorPoints, denormalizeHeight, denormalizeWidth] )

    // ResizeObserver with DPR
    useEffect( () => {
        try {
            const element = containerRef.current
            if ( !element ) return

            const debouncedResizeHandler = debounce( "rovb-resize-handler", ( entries ) => {
                requestAnimationFrame( () => {
                    if ( !bgImgRef.current ) return

                    const img = bgImgRef.current
                    const canvasWrapper = canvasWrapperRef.current
                    const drawCanvas = drawCanvasRef.current
                    const bgCanvas = bgCanvasRef.current
                    const tempCanvas = tempCanvasRef.current

                    if ( !drawCanvas || !bgCanvas || !containerRef.current ) return

                    const aspect = img.naturalWidth / img.naturalHeight
                    const { width: containerWidth, height: containerHeight } = containerRef.current.getBoundingClientRect()

                    let width = containerWidth * 0.9
                    let height = width / aspect
                    const maxHeight = containerHeight * 0.98

                    if ( height > maxHeight ) {
                        height = maxHeight
                        width = height * aspect
                    }

                    canvasWrapper.style.width = `${width}px`
                    canvasWrapper.style.height = `${height}px`

                    if ( !bgCanvas || !drawCanvas || !tempCanvas ) return

                    const dpr = window.devicePixelRatio || 1

                        ;[bgCanvas, drawCanvas, tempCanvas].forEach( ( c ) => {
                            c.width = width * dpr
                            c.height = height * dpr
                            c.style.width = `${width}px`
                            c.style.height = `${height}px`
                            c.style.position = "absolute"
                            c.style.top = 0
                            c.style.left = 0
                        } )

                    bgCanvasCtxRef.current = bgCanvas.getContext( "2d" )
                    drawCanvasCtxRef.current = drawCanvas.getContext( "2d" )
                    tempCanvasCtxRef.current = tempCanvas.getContext( "2d" )

                    redrawAll()
                } )
            }, 300 )

            const observer = new ResizeObserver( debouncedResizeHandler )
            observer.observe( element )

            return () => observer.disconnect()
        } catch ( err ) {
            console.log( "Resize Handler Failed!", err )
        }
    }, [redrawAll] )

    // ---------- Render ----------

    return (
        <Box
            overflow="hidden"
            tabIndex={0}
            width="100%"
            display="flex"
            alignItems="center"
            flexDirection="column"
            height="100%"
            justifyContent="center"
            ref={containerRef}
            sx={{ position: "relative" }}
        >
            <Box
                sx={{ cursor: "default", ":hover .cursors": { display: "block" } }}
                ref={canvasWrapperRef}
                position="relative"
            >
                <Box component="canvas" ref={bgCanvasRef} />
                <Box component="canvas" ref={drawCanvasRef} />
                <Box component="canvas" ref={tempCanvasRef} />
                <Box
                    className="cursors"
                    sx={{ pointerEvents: "none", display: "none" }}
                    fontSize="14px"
                    ref={canvasCursorRef}
                    position="absolute"
                >
                    {tool === "select" && Icons.default.Select}
                    {tool === "pencil" && Icons.default.Pencil}
                    {tool === "pen" && Icons.default.Pen}
                    {tool === "eraser" && Icons.default.Eraser}
                    {tool === "marker" && Icons.default.Highlighter}
                    {tool === "text" && Icons.default.TextCursor}
                    {["rect", "circle"].includes( tool ) && Icons.default.Plus}
                </Box>
            </Box>

            <MenuBar
                readOnly={true}
                downloadMergedCanvas={downloadMergedCanvas}
                toggleFullScreen={toggleFullScreen}
            />
        </Box>
    )
} )

export default ReadOnlyVirtualBoard
